import { getApiEnvironmentTable } from '~/components/ContentTools/apiSkillEnvironments';

const TOUR_FORM_RESOURCES = [
  {
    label: 'Root details',
    resource: '',
    path: '/api/v2/tours/forms/:tourId',
    notes: 'Core tour fields, content, categorisation, payment schedule, and Salesforce-sourced root data.',
  },
  {
    label: 'Tour options',
    resource: 'tour-options',
    path: '/api/v2/tours/forms/:tourId/tour-options',
    notes: 'Tour option details, departures, prices, inclusions, and option-level itinerary data.',
  },
  {
    label: 'Tour images',
    resource: 'tour-images',
    path: '/api/v2/tours/forms/:tourId/tour-images',
    notes: 'Image arrays, hero/hidden flags, ordering, captions, and image metadata.',
  },
  {
    label: 'Tour pricing',
    resource: 'tour-pricing',
    path: '/api/v2/tours/forms/:tourId/tour-pricing',
    notes: 'Pricing table form data.',
  },
  {
    label: 'Tour dynamic pricing',
    resource: 'tour-dynamic-pricing',
    path: '/api/v2/tours/forms/:tourId/tour-dynamic-pricing',
    notes: 'Dynamic pricing configuration.',
  },
  {
    label: 'Fine print',
    resource: 'fine-print',
    path: '/api/v2/tours/forms/:tourId/fine-print',
    notes: 'Fine print content blocks.',
  },
  {
    label: 'Traveller requirements',
    resource: 'traveller-requirements',
    path: '/api/v2/tours/forms/:tourId/traveller-requirements',
    notes: 'Traveller requirements form data.',
  },
  {
    label: 'Campaign schedule',
    resource: 'campaign-schedule',
    path: '/api/v2/tours/forms/:tourId/campaign-schedule',
    notes: 'Campaign and schedule fields.',
  },
  {
    label: 'Inventory',
    resource: 'inventory',
    path: '/api/v2/tours/forms/:tourId/inventory',
    notes: 'Read-only inventory form data in Admin Portal.',
  },
  {
    label: 'Optional extras',
    resource: 'optional-extras',
    path: '/api/v2/tours/forms/:tourId/optional-extras',
    notes: 'Optional extras configuration.',
  },
  {
    label: 'User review linked tours',
    resource: 'user-review-linked-tours',
    path: '/api/v2/tours/forms/:tourId/user-review-linked-tours',
    notes: 'Linked tours for user reviews.',
  },
  {
    label: 'Departures',
    resource: 'departures',
    path: '/api/v2/tours/forms/:tourId/departures',
    notes: 'Departure data used when editing itineraries by departure.',
  },
];

export function getToursCodexSkillText() {
  const environmentTable = getApiEnvironmentTable();
  const resourceTable = TOUR_FORM_RESOURCES.map(
    ({ label, resource, path, notes }) => `| ${label} | \`${resource || 'root'}\` | \`${path}\` | ${notes} |`,
  ).join('\n');

  return `# Luxury Escapes Admin Tours API Editing Skill

Use this skill when a Luxury Escapes content user asks Codex to inspect or edit a tour through the Admin Portal API instead of navigating the browser UI.

## Inputs to ask the user for

- The API details block copied from the Admin Portal. It should include \`API_BASE_URL\` and \`ADMIN_BEARER_TOKEN\`.
- Which environment they want edited: \`test\` or \`prod\`.
- The tour ID, usually from an Admin URL like \`/tours/details/:tourId\`.
- The exact content change requested.

Never ask the user to paste long-lived credentials. Never print the bearer token back to the user. Keep it only in memory for the current task.

## API hosts

Do not call the Admin Portal web-app host, such as \`https://test-admin.luxuryescapes.com\` or \`https://admin.luxuryescapes.com\`. The Admin Portal talks to a separate API host. Prefer the copied \`API_BASE_URL\` value from the Admin Portal. If the user did not paste API details, choose the API base URL from the environment:

| Environment | API base URL | Use for |
| --- | --- | --- |
${environmentTable}

If the user is working from a review app or a local setup with a custom backend, ask them to use the Admin Portal's "Copy API details" button so you get the exact configured \`window.configs.API_HOST\` value and a matching token.

## Authentication

Write \`ADMIN_BEARER_TOKEN\` to a file once (as in the copied API details), then load it with \`$(cat /tmp/le-admin-bearer.token)\` for every request. Do not paste the JWT into curl \`-H\` headers. Send every API request with:

\`\`\`http
Authorization: Bearer <ADMIN_TOKEN>
Content-Type: application/json;charset=UTF-8
\`\`\`

## Form resources

Each editable resource returns a JSON object with \`result.schema\`, \`result.uiSchema\`, and \`result.formData\`. Fetch the current resource first, edit only the relevant part of \`result.formData\`, then PATCH the full edited form data back to the same resource URL unless the user explicitly asks for a broader change.

| Area | Resource key | URL path | Use for |
| --- | --- | --- | --- |
${resourceTable}

The base URL for requests is:

\`\`\`text
<API_BASE_URL><URL path above>
\`\`\`

Replace \`:tourId\` with the tour ID. If a request unexpectedly returns the Admin HTML shell instead of JSON, the host is wrong; switch from the Admin Portal web-app host to the API base URL for that environment.

## Workflow

1. Identify the smallest resource that contains the requested content.
2. GET that resource and inspect \`result.schema\` plus \`result.formData\`; use the schema titles and field names to locate the right field.
3. Make a minimal JSON edit. Preserve all unrelated fields, IDs, ordering, booleans, arrays, and object structure.
4. Show the user a concise before/after summary before PATCHing, unless they already asked you to apply directly.
5. PATCH the full edited \`formData\` JSON to the same resource URL.
6. GET the same resource again and verify the expected content is present.
7. If the user asks to test a change and revert it, capture the original value first, PATCH the test value, verify, then PATCH the original value back and verify again.

## Safety rules

- Prefer GET before PATCH. Do not construct payloads from memory.
- Do not edit inventory unless the API explicitly supports PATCH for the selected environment.
- Do not modify images, prices, departures, or payment schedules unless the user specifically asks.
- For itinerary-by-departure edits, first inspect \`tour-options\` and \`departures\`; some departure-specific itinerary updates use dedicated endpoints in the Admin implementation.
- If the schema or form data does not clearly identify the target field, stop and ask for the exact field or tab instead of guessing.
- If a PATCH fails, report the status and response body without retrying destructive changes blindly.
`;
}
